import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Row, Card, Alert, Spinner, Col, Form, Button, Badge } from 'react-bootstrap';
import { fetchWeerepo, postWeerepoComment } from '../../../../firebase-config';
import Roles from '../../../../roles';

/**
 * ウィーレポアーカイブ画面.
 *
 * @param {employee} 従業員
 * @returns
 */
function WeeRepoArchivePage() {
    const { state } = useLocation();
    const [list, setList] = useState([]);
    const { myEmployee, employee } = state;

    const [showError, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(true);
    const [commentMap, setCommentMap] = useState({});
    const [commentLoadingMap, setCommentLoadingMap] = useState({});
    const [commentErrorMap, setCommentErrorMap] = useState({});
    const [commentSuccessMap, setCommentSuccessMap] = useState({});

    const ITEMS_PER_PAGE = 20;

    const isAdmin = (roleId) => {
        for (const role of Object.values(Roles)) {
            if (role.id === roleId) {
                return role === Roles.ADMIN;
            }
        }
        return false;
    };

    /**
     * ウィーレポ一覧を取得.
     */
    const getWeeRepoList = async (currentPage = 0) => {
        setLoading(true);

        try {
            const response = await fetchWeerepo(employee.id, ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

            if (response) {
                setList((prevList) => [...prevList, ...response.results]); // データを追加
                setHasNext(response.hasNext); // 次のページがあるかを更新
                setError(false);
            } else {
                setError(true);
            }
        } catch (error) {
            console.error("Error fetching previous data:", error);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (seconds) => {
        const date = new Date(seconds * 1000);
        const formatter = new Intl.DateTimeFormat('ja-JP', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: 'Asia/Tokyo',
        });
        return formatter.format(date);
    };

    const handleCommentChange = (id, value) => {
        setCommentMap((prev) => ({ ...prev, [id]: value }));
    };

    /**
     * ウィーレポに対してコメント送信.
     *
     * @param {Event} e
     * @param {string} archiveId
     */
    const handleCommentSubmit = async (e, archiveId) => {
        e.preventDefault();
        const comment = commentMap[archiveId] || '';
        if (!comment.trim()) return;

        setCommentLoadingMap((prev) => ({ ...prev, [archiveId]: true }));
        setCommentErrorMap((prev) => ({ ...prev, [archiveId]: false }));
        setCommentSuccessMap((prev) => ({ ...prev, [archiveId]: false }));

        try {
            await postWeerepoComment(archiveId, myEmployee.id, comment);
            setCommentSuccessMap((prev) => ({ ...prev, [archiveId]: true }));
            setCommentMap((prev) => ({ ...prev, [archiveId]: '' }));
            window.location.reload();
        } catch (error) {
            console.error("Error posting comment:", error);
            setCommentErrorMap((prev) => ({ ...prev, [archiveId]: true }));
        } finally {
            setCommentLoadingMap((prev) => ({ ...prev, [archiveId]: false }));
        }
    };

    /**
     * 改行コードをdiv区切りで変換.
     * 
     * @param {string} str 
     * @returns 改行された文字列
     */
    function convertBr(str) {
        if (!str) return null;
        return str.split("\n").map((line, index) => {
            return line === "" ? <div key={index}>&nbsp;</div> : <div key={index}>{line}</div>;
        });
    }

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "ウィーレポ アーカイブ - " + employee.lastName + ' ' + employee.firstName;
        getWeeRepoList();
    }, [employee]);

    return (
        <Row>
            <h1>ウィーレポ アーカイブ - {employee.lastName} {employee.firstName}</h1>

            <div className='mt-4'>
                {list
                    .filter((archive) => archive.data != null)
                    .map((archive, index) => {
                        const data = archive.data;
                        return (
                            <Card key={archive.id} className='mb-4'>
                                <Card.Header className='d-flex align-items-center gap-2'>
                                    <strong>#{index + 1}</strong>
                                    <span>{formatDate(data.registerTime._seconds)}</span>
                                    {data.comment && <Badge bg="info">コメントあり</Badge>}
                                </Card.Header>
                                <Card.Body>
                                    <Row className='mb-3'>
                                        <Col xs={6}>
                                            <p className='mb-1'><strong>業務量</strong></p>
                                            <p>{data.workEmoji.label} {data.workEmoji.state}</p>
                                        </Col>
                                        <Col xs={6}>
                                            <p className='mb-1'><strong>メンタル</strong></p>
                                            <p>{data.mentalEmoji.label} {data.mentalEmoji.state}</p>
                                        </Col>
                                    </Row>
                                    <Row className='mb-3'>
                                        <Col xs={6}>
                                            <p className='mb-1'><strong>現場</strong></p>
                                            <ul>
                                                {data.works?.map((str, i) => (
                                                    <li key={i}>{str}</li>
                                                ))}
                                            </ul>
                                        </Col>
                                        <Col xs={6}>
                                            <p className='mb-1'><strong>自社</strong></p>
                                            <ul>
                                                {data.insideWorks?.map((str, i) => (
                                                    <li key={i}>{str}</li>
                                                ))}
                                            </ul>
                                        </Col>
                                    </Row>
                                    <p className='mb-1'><strong>連絡事項</strong></p>
                                    <p>{convertBr(data.content)}</p>
                                    {data.comment ? (
                                        <>
                                            <p className='mb-1'><strong>コメント</strong></p>
                                            <p>{convertBr(data.comment.content)}</p>
                                        </>
                                    ) : (
                                        isAdmin(myEmployee.role) && (
                                            <Form onSubmit={(e) => handleCommentSubmit(e, archive.id)}>
                                                <Form.Group controlId={`formComment-${archive.id}`}>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={4}
                                                        value={commentMap[archive.id] || ''}
                                                        onChange={(e) => handleCommentChange(archive.id, e.target.value)}
                                                        placeholder="コメントを入力"
                                                        required
                                                    />
                                                </Form.Group>
                                                <Button variant="primary" type="submit" className='mt-2' disabled={commentLoadingMap[archive.id]}>
                                                    {commentLoadingMap[archive.id] ? '送信中...' : '送信'}
                                                </Button>
                                                {commentSuccessMap[archive.id] && <Alert variant="success" className="mt-2">コメントを送信しました。</Alert>}
                                                {commentErrorMap[archive.id] && <Alert variant="danger" className="mt-2">コメントの送信に失敗しました。</Alert>}
                                            </Form>
                                        )
                                    )}
                                </Card.Body>
                            </Card>
                        );
                    })
                }
            </div>

            {loading &&
                <div className="d-flex align-items-center justify-content-center">
                    <Spinner animation="border" variant="dark" />
                </div>
            }

            <div className="d-flex justify-content-center mt-4">
                {hasNext && !loading && (
                    <Button onClick={() => {
                        setPage((prevPage) => {
                            const nextPage = prevPage + 1;
                            getWeeRepoList(nextPage);
                            return nextPage;
                        });
                    }}>
                        次を読み込む
                    </Button>
                )}
            </div>

            {showError && <Alert variant="danger">取得失敗しました。</Alert>}
        </Row>
    );
}

export default WeeRepoArchivePage;
